import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, GitCompareArrows, LucideIcon, ScanSearch } from 'lucide-react';
import apiService, { type ApiDomain } from '../../services/api';
import { DocumentExporter } from '../../word/utils/DocumentExporter';
import { DocumentReader } from '../../word/api/DocumentReader';
import { requestCache } from '../../services/RequestCache';
import { directionForLanguage } from '../../i18n/config';
import './MasterCrossChecksInitiation.css';
import './ProjectSetup.css';

/** Log to console with timestamp (e.g. [5:23:47 PM] [LOG] message) for debugging initiation flow */
function logInitiation(message: string, data?: Record<string, unknown>): void {
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: '2-digit', second: '2-digit' });
  const payload = data ? ` ${JSON.stringify(data)}` : '';
  console.log(`[${time}] [LOG] [MasterCrossChecksInitiation] ${message}${payload}`);
}

export type MasterCrossChecksWorkflowVariant = 'master_cross_checks' | 'construction_loan';

export type ReviewMode = 'review_and_respond_redlines' | 'first_pass_legal_review';

const REVIEW_MODE_OPTIONS: {
  value: ReviewMode;
  Icon: LucideIcon;
}[] = [
  {
    value: 'review_and_respond_redlines',
    Icon: GitCompareArrows,
  },
  {
    value: 'first_pass_legal_review',
    Icon: ScanSearch,
  },
];

interface MasterCrossChecksInitiationProps {
  projectId: string;
  onComplete: (subProjectId: string) => void;
  onCancel: () => void;
  /** User chose "Later" on Start Cross-Check — stay on results without re-prompting until sub-project changes. */
  onLater?: (subProjectId: string) => void;
  /** Construction Loan Agreement matches web dashboard upload + track-changes follow-up; MCC skips TC gate. */
  workflowVariant?: MasterCrossChecksWorkflowVariant;
  /** Resume Start Cross-Check for an existing initialized sub-project (user chose "Later" earlier). */
  resumeSubProjectId?: string;
  /**
   * Called when the background auto-start cross-check fails with a validation error (HTTP 400).
   * Fires asynchronously after the user has already been navigated to the results view, so the
   * parent should navigate the user back and display the error message.
   */
  onError?: (message: string) => void;
}

type Phase =
  | 'name_input'
  | 'creating'
  | 'draft_question'
  | 'related_project'
  | 'identify_sides'
  | 'side_assignments'
  | 'start_cross_check';

interface SiblingSubProject {
  id: string;
  name: string;
}

export const MasterCrossChecksInitiation: React.FC<MasterCrossChecksInitiationProps> = ({
  projectId,
  onComplete,
  onCancel,
  workflowVariant = 'master_cross_checks',
  resumeSubProjectId,
  onLater,
  onError,
}) => {
  const { t, i18n } = useTranslation('mcc');
  const chromeDir = directionForLanguage(i18n.language);
  const reviewModeLabel = (value: ReviewMode) => t(`initiation.reviewModes.${value}.label`);
  const reviewModeDescription = (value: ReviewMode) => t(`initiation.reviewModes.${value}.description`);
  const [phase, setPhase] = React.useState<Phase>(resumeSubProjectId ? 'start_cross_check' : 'name_input');
  const [subProjectName, setSubProjectName] = React.useState('');
  const [suggestedName, setSuggestedName] = React.useState('');
  const [createdSubProjectId, setCreatedSubProjectId] = React.useState<string | null>(
    resumeSubProjectId ?? null
  );
  const [error, setError] = React.useState<string | null>(null);
  const [progressMessage, setProgressMessage] = React.useState('');

  // Draft question / related project state
  const [siblingSubProjects, setSiblingSubProjects] = React.useState<SiblingSubProject[]>([]);
  const [selectedParentId, setSelectedParentId] = React.useState<string | null>(null);
  const [isLoadingSiblings, setIsLoadingSiblings] = React.useState(false);
  const [isLinking, setIsLinking] = React.useState(false);

  // Identify sides state
  const [uniqueAuthors, setUniqueAuthors] = React.useState<string[]>([]);
  const [sideAssignments, setSideAssignments] = React.useState<Record<string, 'our_side' | 'their_side'>>({});
  const [_isIdentifying, setIsIdentifying] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Start cross-check state
  const [isStartingCrossCheck, setIsStartingCrossCheck] = React.useState(false);
  const [reviewMode, setReviewMode] = React.useState<ReviewMode | ''>('');
  const [reviewModeMenuOpen, setReviewModeMenuOpen] = React.useState(false);
  const reviewModeDropdownRef = React.useRef<HTMLDivElement>(null);

  const [uploadedClientFileName, setUploadedClientFileName] = React.useState<string | null>(null);
  const isConstructionLoanFlow = workflowVariant === 'construction_loan';
  // Domain is known the moment the user picks a solution, so route API calls explicitly
  // (Master Cross Check => real-estate) instead of probing both trees and eating a 400.
  const domainHint: ApiDomain = isConstructionLoanFlow ? 'construction-loan' : 'real-estate';
  const selectedReviewModeOption = REVIEW_MODE_OPTIONS.find((option) => option.value === reviewMode);

  React.useEffect(() => {
    if (!reviewModeMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (reviewModeDropdownRef.current?.contains(e.target as Node)) return;
      setReviewModeMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setReviewModeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [reviewModeMenuOpen]);

  React.useEffect(() => {
    if (resumeSubProjectId) return;
    const loadFilename = async () => {
      try {
        const filename = await DocumentExporter.getDocumentNameWithoutExtension();
        if (filename && filename !== 'document') {
          setSuggestedName(filename);
          setSubProjectName(filename);
        } else {
          setSuggestedName(t('initiation.defaultName'));
          setSubProjectName(t('initiation.defaultName'));
        }
      } catch {
        setSuggestedName(t('initiation.defaultName'));
        setSubProjectName(t('initiation.defaultName'));
      }
    };
    loadFilename();
  }, [resumeSubProjectId, t]);

  const handleCreate = async () => {
    if (!subProjectName.trim()) {
      setError(t('initiation.nameRequired'));
      return;
    }

    setPhase('creating');
    setError(null);

    try {
      // Step 1: Create sub-project
      setProgressMessage(t('initiation.progressCreating'));
      const createResponse = await apiService.createSubProject(projectId, subProjectName.trim(), undefined, domainHint);
      if (!createResponse.success || !createResponse.data) {
        throw new Error(t('initiation.createFailed'));
      }
      const newSubProjectId = createResponse.data.id;
      setCreatedSubProjectId(newSubProjectId);
      logInitiation('Sub-project created', { subProjectId: newSubProjectId, name: subProjectName.trim() });

      // Step 2: Copy master documents from parent project
      setProgressMessage(t('initiation.progressCopying'));
      try {
        const copyRes = await apiService.copyMasterDocumentsToSubProject(newSubProjectId, domainHint);
        const copiedCount = Array.isArray(copyRes?.data) ? copyRes.data.length : 0;
        logInitiation('Copy master documents completed', {
          copiedCount,
          fileTypes: Array.isArray(copyRes?.data)
            ? copyRes.data.map((f: { file_type?: string }) => f.file_type).filter(Boolean)
            : [],
        });
      } catch (copyErr: any) {
        logInitiation('Copy master documents warning (continuing)', {
          message: String(copyErr),
          status: copyErr?.response?.status,
          detail: copyErr?.response?.data?.detail || copyErr?.response?.data?.message,
        });
      }

      // Step 3: Upload current Word document as Client Requests
      setProgressMessage(t('initiation.progressUploading'));
      try {
        const documentFilename = await DocumentExporter.getDocumentName();
        const documentFile = await DocumentExporter.getDocumentAsFile(documentFilename);
        const uploadRes = await apiService.uploadDocument(
          projectId,
          newSubProjectId,
          documentFile,
          'Client Requests',
          domainHint
        );
        if (!uploadRes.success || !uploadRes.data) {
          throw new Error(uploadRes.message || t('initiation.createFailed'));
        }
        setUploadedClientFileName(documentFile.name || documentFilename || null);

        if (isConstructionLoanFlow) {
          setProgressMessage(t('initiation.progressTrackChanges'));
          const storagePath = (uploadRes.data as { storage_path?: string }).storage_path;
          const fileId = (uploadRes.data as { id?: string }).id;
          let hasTrackChanges = false;
          if (storagePath) {
            try {
              const tcRes = await apiService.analyzeTrackChanges(storagePath, projectId, fileId);
              const n = tcRes?.data?.paragraphs_with_track_changes ?? 0;
              hasTrackChanges = typeof n === 'number' ? n > 0 : false;
            } catch {
              // non-blocking: if analysis fails, still allow flow (dashboard treats similarly)
            }
          }
          if (!hasTrackChanges) {
            throw new Error(t('initiation.trackChangesRequired'));
          }
        }
      } catch (uploadErr: any) {
        const uploadErrorMsg = uploadErr.response?.data?.detail || uploadErr.message || '';
        if (!uploadErrorMsg.includes('already exists') && !uploadErrorMsg.includes('duplicate')) {
          throw new Error(
            uploadErr.message === t('initiation.trackChangesRequired')
              ? uploadErr.message
              : t('initiation.uploadFailed', { detail: uploadErrorMsg })
          );
        }
        logInitiation('Upload document (file already exists, continuing)');
      }
      logInitiation('Client Requests document uploaded');

      // Step 3b (CL only): Save clean loan agreement text from the live Word document
      // (reject-all OOXML semantics) to the newly created sub-project.
      if (isConstructionLoanFlow) {
        setProgressMessage(t('initiation.progressLoanContext'));
        try {
          const { fullText, paragraphs } = await DocumentReader.getFullDocumentContextOriginal();
          if (fullText.trim()) {
            await apiService.saveLoanAgreementContext(newSubProjectId, fullText, paragraphs);
            logInitiation('Loan agreement inline context saved', { paragraphCount: paragraphs.length });
          } else {
            logInitiation('Loan agreement inline context is empty – skipping');
          }
        } catch (loanErr: any) {
          logInitiation('Warning: loan agreement context save failed (continuing)', { error: String(loanErr) });
        }
      }

      // Invalidate sub-projects cache so the list refreshes
      requestCache.invalidate(`sub-projects-${projectId}`);

      // Step 4: Show draft question
      setPhase('draft_question');
    } catch (err: any) {
      logInitiation('Error during sub-project creation', { status: err.response?.status, detail: err.response?.data?.detail || err.message });
      setError(err.response?.data?.detail || err.message || t('initiation.createFailed'));
      setPhase('name_input');
    }
  };

  const handleDraftYes = async () => {
    setIsLoadingSiblings(true);
    setPhase('related_project');
    setSelectedParentId(null);

    try {
      const response = await apiService.getProjectDetail(projectId, domainHint);
      if (response.success && response.data?.sub_projects) {
        const siblings = response.data.sub_projects
          .filter((sp: any) => sp.id !== createdSubProjectId && !sp.sub_project_metadata?.parent)
          .map((sp: any) => ({ id: sp.id, name: sp.name }));
        setSiblingSubProjects(siblings);
      }
    } catch (err) {
      console.error('Error fetching sibling sub-projects:', err);
      setError(t('initiation.loadProjectsFailed'));
    } finally {
      setIsLoadingSiblings(false);
    }
  };

  const handleDraftNo = async () => {
    await proceedToIdentifySides();
  };

  const handleRelatedProjectContinue = async (parentId: string | null) => {
    if (!createdSubProjectId) return;

    if (parentId) {
      setIsLinking(true);
      try {
        await apiService.linkToParent(createdSubProjectId, parentId, domainHint);
      } catch (err: any) {
        console.error('Error linking to parent:', err);
        setError(err.response?.data?.detail || t('initiation.linkFailed'));
        setIsLinking(false);
        return;
      }
      setIsLinking(false);
    }

    await proceedToIdentifySides();
  };

  const proceedToIdentifySides = async () => {
    if (!createdSubProjectId) return;

    setPhase('identify_sides');
    setIsIdentifying(true);
    setError(null);

    try {
      const authors = await apiService.identifySides(createdSubProjectId, domainHint);
      logInitiation('Identify sides completed', { authorCount: authors.length });
      if (authors.length > 0) {
        setUniqueAuthors(authors);
        setPhase('side_assignments');
      } else {
        // No authors found - go to Start Cross-Check step (auto-start will run as Module A)
        logInitiation('No authors from identify-sides, showing Start Cross-Check step');
        setPhase('start_cross_check');
      }
    } catch (err: any) {
      console.error('Error identifying sides:', err);
      const errorMessage = err.response?.data?.detail || err.message || t('initiation.identifyFailed');
      setError(errorMessage);
      // Still allow user to proceed even on error
      setPhase('side_assignments');
    } finally {
      setIsIdentifying(false);
    }
  };

  const handleSaveSideAssignments = async () => {
    if (!createdSubProjectId) return;

    setIsSaving(true);
    setError(null);
    logInitiation('Saving side assignments', { subProjectId: createdSubProjectId, authorCount: Object.keys(sideAssignments).length });

    try {
      await apiService.saveSideAssignments(createdSubProjectId, sideAssignments, domainHint);
      logInitiation('Side assignments saved successfully (backend sets module_type=module-b)');
      setPhase('start_cross_check');
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      logInitiation('Error saving side assignments', { status: err.response?.status, detail });
      setError(detail || t('initiation.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartCrossCheck = async () => {
    if (!createdSubProjectId) return;
    if (isConstructionLoanFlow && !reviewMode) return;

    const clReviewMode: ReviewMode | undefined =
      isConstructionLoanFlow && reviewMode ? reviewMode : undefined;

    setIsStartingCrossCheck(true);
    setError(null);
    logInitiation('Start Cross-Check clicked', { subProjectId: createdSubProjectId, reviewMode });

    // Transition to the project view immediately — CrossCheckResultsView handles all
    // live progress via its own polling loop. The HTTP call runs in the background.
    const subProjectId = createdSubProjectId;
    onComplete(subProjectId);

    // Extract paragraphs and fire auto-start as a background task (no await).
    (async () => {
      let payload: Parameters<typeof apiService.autoStartCrossCheck>[1] | undefined;

      if (isConstructionLoanFlow && clReviewMode) {
        if (clReviewMode === 'first_pass_legal_review') {
          try {
            logInitiation('Saving loan agreement inline context (reject-all) for first-pass review');
            const { fullText, paragraphs } = await DocumentReader.getFullDocumentContextOriginal();
            if (!fullText.trim()) {
              throw new Error(t('initiation.noDocumentText'));
            }
            await apiService.saveLoanAgreementContext(subProjectId, fullText, paragraphs);
            logInitiation('Loan agreement inline context saved', { paragraphCount: paragraphs.length });

            logInitiation('Extracting all paragraphs from live document for first-pass review');
            const paragraphsWithIndices = await DocumentReader.getParagraphsWithIndices();
            const nonEmpty = paragraphsWithIndices
              .filter((p) => p.text.trim())
              .map((p) => ({ paragraph_index: p.index, text: p.text.trim() }));
            logInitiation('Office.js paragraph extraction complete', { paragraphCount: nonEmpty.length });
            if (nonEmpty.length === 0) {
              throw new Error(t('initiation.noParagraphs'));
            }
            payload = {
              review_mode: clReviewMode,
              paragraphs: nonEmpty,
            };
          } catch (extractErr: any) {
            const msg = extractErr?.message || String(extractErr);
            logInitiation('First-pass paragraph extraction failed', { error: msg });
            if (onError) {
              onError(msg || t('initiation.extractFailed'));
            }
            return;
          }
        } else {
          try {
            logInitiation('Extracting track-change paragraphs from live document via Office.js');
            const extracted = await DocumentReader.extractTrackChangedParagraphs();
            const tableTcCount = extracted.paragraphsWithTrackChanges.filter(
              (p) => p.paragraphXml.includes('<w:tbl') || p.paragraphXml.includes('w:tc')
            ).length;
            logInitiation('Office.js extraction complete', {
              totalParagraphs: extracted.allParagraphTexts.length,
              tcParagraphs: extracted.paragraphsWithTrackChanges.length,
              tableTcParagraphs: tableTcCount,
              tcTextPreviews: extracted.paragraphsWithTrackChanges.slice(0, 5).map((p) => ({
                index: p.paragraphIndex,
                editedPreview: p.editedText.slice(0, 80),
                inTableXml: p.paragraphXml.includes('w:tc') || p.paragraphXml.includes('<w:tbl'),
              })),
            });
            payload = { review_mode: clReviewMode };
            if (extracted.paragraphsWithTrackChanges.length > 0) {
              payload = {
                ...payload,
                pre_extracted_paragraphs: extracted.paragraphsWithTrackChanges.map((p) => ({
                  paragraph_index: p.paragraphIndex,
                  paragraph_xml: p.paragraphXml,
                  original_text: p.originalText,
                  edited_text: p.editedText,
                  participants: p.participants,
                })),
                all_paragraph_texts: extracted.allParagraphTexts,
              };
            } else {
              logInitiation('No TC paragraphs via Office.js – falling back to file-based identification');
            }
          } catch (extractErr) {
            logInitiation('Office.js extraction failed – falling back to file-based identification', { error: String(extractErr) });
            payload = { review_mode: clReviewMode };
          }
        }
      }

      try {
        await apiService.autoStartCrossCheck(subProjectId, payload, domainHint);
        logInitiation('Auto-start cross-check call returned');
      } catch (err: any) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message;
        logInitiation('Auto-start cross-check call failed (user already in results view)', { status, error: detail });
        if (onError) {
          const msg =
            typeof detail === 'string' && detail.trim()
              ? detail
              : t('initiation.workflowFailed');
          onError(msg);
        }
      }
    })();
  };

  const handleLater = () => {
    if (!createdSubProjectId) return;
    if (onLater) {
      onLater(createdSubProjectId);
      return;
    }
    onComplete(createdSubProjectId);
  };

  const allAssigned = uniqueAuthors.length > 0 && uniqueAuthors.every((a) => sideAssignments[a]);

  // Phase: Name Input
  if (phase === 'name_input') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.createTitle')}
          </div>
          <p className="mcc-initiation-description">{t('initiation.createDescription')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="create-subproject-form">
          <div className="form-group">
            <label htmlFor="mcc-subproject-name">{t('initiation.projectName')}</label>
            <input
              id="mcc-subproject-name"
              type="text"
              dir="auto"
              value={subProjectName}
              onChange={(e) => setSubProjectName(e.target.value)}
              placeholder={suggestedName || t('initiation.projectNamePlaceholder')}
              className="subproject-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && subProjectName.trim()) {
                  handleCreate();
                }
              }}
            />
          </div>
          <div className="mcc-initiation-buttons">
            <button onClick={onCancel} className="button-secondary">
              {t('initiation.cancel')}
            </button>
            <button
              onClick={handleCreate}
              className="button-primary"
              disabled={!subProjectName.trim()}
            >
              {t('initiation.create')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Phase: Creating (loading)
  if (phase === 'creating') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.settingUp')}
          </div>
        </div>
        <div className="project-setup-loading">
          <div className="spinner"></div>
          <span>{progressMessage}</span>
        </div>
      </div>
    );
  }

  // Phase: Draft Question
  if (phase === 'draft_question') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.draftTitle')}
          </div>
          {isConstructionLoanFlow && uploadedClientFileName ? (
            <p className="mcc-initiation-description">
              {t('initiation.draftFileContains', { name: uploadedClientFileName })}
            </p>
          ) : null}
          <p className="mcc-initiation-description">{t('initiation.draftQuestion')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="mcc-initiation-buttons">
          <button onClick={handleDraftNo} className="button-secondary">
            {isConstructionLoanFlow ? t('initiation.totallyNew') : t('initiation.no')}
          </button>
          <button onClick={handleDraftYes} className="button-primary">
            {t('initiation.yes')}
          </button>
        </div>
      </div>
    );
  }

  // Phase: Related Project Selection
  if (phase === 'related_project') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.relatedTitle')}
          </div>
          <p className="mcc-initiation-description">{t('initiation.relatedDescription')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoadingSiblings ? (
          <div className="project-setup-loading">
            <div className="spinner"></div>
            <span>{t('initiation.loadingProjects')}</span>
          </div>
        ) : (
          <>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <select
                value={selectedParentId || ''}
                onChange={(e) => setSelectedParentId(e.target.value || null)}
                className="subproject-name-input"
                style={{ width: '100%' }}
              >
                <option value="">{t('initiation.selectProject')}</option>
                {siblingSubProjects.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mcc-initiation-buttons">
              <button
                onClick={() => handleRelatedProjectContinue(null)}
                className="button-secondary"
                disabled={isLinking}
              >
                {t('initiation.totallyNew')}
              </button>
              <button
                onClick={() => handleRelatedProjectContinue(selectedParentId)}
                className="button-primary"
                disabled={isLinking || !selectedParentId}
              >
                {isLinking ? t('initiation.linking') : t('initiation.continue')}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Phase: Identifying Sides (loading)
  if (phase === 'identify_sides') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.extractingTitle')}
          </div>
        </div>
        <div className="project-setup-loading">
          <div className="spinner"></div>
          <span>{t('initiation.extracting')}</span>
        </div>
      </div>
    );
  }

  // Phase: Side Assignments
  if (phase === 'side_assignments') {
    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-initiation-header">
          <div className="mcc-initiation-title" role="heading" aria-level={3}>
            {t('initiation.identifyTitle')}
          </div>
          <p className="mcc-initiation-description">
            {uniqueAuthors.length > 0
              ? t('initiation.foundAuthors', { count: uniqueAuthors.length })
              : t('initiation.noAuthors')}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {uniqueAuthors.length > 0 ? (
          <>
            <div className="assignments-list">
              {uniqueAuthors.map((author) => (
                <div key={author} className="assignment-item" dir="ltr">
                  <span className="author-name" dir="auto">
                    {author}
                  </span>
                  <div className="assignment-buttons">
                    <button
                      onClick={() =>
                        setSideAssignments((prev) => ({ ...prev, [author]: 'our_side' }))
                      }
                      className={`assignment-button ${sideAssignments[author] === 'our_side' ? 'active our-side' : ''}`}
                    >
                      {t('initiation.ourSide')}
                    </button>
                    <button
                      onClick={() =>
                        setSideAssignments((prev) => ({ ...prev, [author]: 'their_side' }))
                      }
                      className={`assignment-button ${sideAssignments[author] === 'their_side' ? 'active their-side' : ''}`}
                    >
                      {t('initiation.theirSide')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mcc-initiation-buttons">
              <button
                onClick={handleSaveSideAssignments}
                disabled={!allAssigned || isSaving}
                className="button-primary save-assignments-button"
              >
                {isSaving
                  ? t('initiation.saving')
                  : allAssigned
                    ? t('initiation.saveContinue')
                    : t('initiation.assignAll', {
                        done: uniqueAuthors.filter((a) => sideAssignments[a]).length,
                        total: uniqueAuthors.length,
                      })}
              </button>
            </div>
          </>
        ) : (
          <div className="mcc-initiation-buttons">
            <button
              onClick={() => setPhase('start_cross_check')}
              className="button-primary"
            >
              {t('initiation.continue')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Phase: Start Cross-Check (success + Start Cross-Check / Later)
  if (phase === 'start_cross_check') {
    const preStartSteps = [
      { key: 'identify_sides', label: t('results.steps.identifySides'), status: 'complete' as const },
      { key: 'identify_requests', label: t('results.steps.identifyClientRequests'), status: 'pending' as const },
      { key: 'cross_check', label: t('results.steps.crossCheckAnalysis'), status: 'pending' as const },
    ];

    return (
      <div className="mcc-initiation" dir={chromeDir}>
        <div className="mcc-followup-steps">
          {preStartSteps.map((step) => (
            <div key={step.key} className={`mcc-followup-step mcc-followup-${step.status}`}>
              <span className="mcc-followup-arrow" aria-hidden>&#8250;</span>
              <span className="mcc-followup-icon" aria-hidden>
                {step.status === 'complete' ? '✓' : '○'}
              </span>
              <span className="mcc-followup-label">{step.label}</span>
            </div>
          ))}
        </div>

        {isConstructionLoanFlow && (
          <div className="mcc-review-mode-section">
            <div className="mcc-review-mode-card">
              <div className="mcc-review-mode-header">
                <span className="mcc-review-mode-title">{t('initiation.reviewMode')}</span>
                <span className="mcc-review-mode-badge">{t('initiation.required')}</span>
              </div>

              <div
                ref={reviewModeDropdownRef}
                className="mcc-review-mode-dropdown"
                data-open={reviewModeMenuOpen ? 'true' : 'false'}
              >
                <button
                  type="button"
                  id="review-mode-select"
                  className={`mcc-review-mode-trigger${reviewMode ? ' mcc-review-mode-trigger--selected' : ' mcc-review-mode-trigger--placeholder'}`}
                  disabled={isStartingCrossCheck}
                  aria-haspopup="listbox"
                  aria-expanded={reviewModeMenuOpen}
                  aria-label={t('initiation.reviewModeAria')}
                  onClick={() => !isStartingCrossCheck && setReviewModeMenuOpen((open) => !open)}
                >
                  {selectedReviewModeOption ? (
                    <span className="mcc-review-mode-trigger-icon" aria-hidden>
                      <selectedReviewModeOption.Icon size={15} strokeWidth={2} />
                    </span>
                  ) : (
                    <span className="mcc-review-mode-trigger-icon mcc-review-mode-trigger-icon--muted" aria-hidden>
                      <ScanSearch size={15} strokeWidth={2} />
                    </span>
                  )}
                  <span className="mcc-review-mode-trigger-text">
                    {selectedReviewModeOption
                      ? reviewModeLabel(selectedReviewModeOption.value)
                      : t('initiation.chooseReviewMode')}
                  </span>
                  <ChevronDown
                    size={16}
                    strokeWidth={2}
                    className={`mcc-review-mode-chevron${reviewModeMenuOpen ? ' mcc-review-mode-chevron--open' : ''}`}
                    aria-hidden
                  />
                </button>

                {reviewModeMenuOpen && (
                  <ul className="mcc-review-mode-menu" role="listbox" aria-label={t('initiation.chooseReviewModeAria')}>
                    {REVIEW_MODE_OPTIONS.map((option) => (
                      <li key={option.value} role="option" aria-selected={reviewMode === option.value}>
                        <button
                          type="button"
                          className={`mcc-review-mode-option${reviewMode === option.value ? ' mcc-review-mode-option--active' : ''}`}
                          onClick={() => {
                            setReviewMode(option.value);
                            setReviewModeMenuOpen(false);
                          }}
                        >
                          <span className="mcc-review-mode-option-icon" aria-hidden>
                            <option.Icon size={16} strokeWidth={2} />
                          </span>
                          <span className="mcc-review-mode-option-copy">
                            <span className="mcc-review-mode-option-title">{reviewModeLabel(option.value)}</span>
                            <span className="mcc-review-mode-option-desc">{reviewModeDescription(option.value)}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {selectedReviewModeOption && (
                <p className="mcc-review-mode-summary">{reviewModeDescription(selectedReviewModeOption.value)}</p>
              )}
            </div>
          </div>
        )}

        <div className="mcc-success-block">
          <div className="mcc-success-icon" aria-hidden>
            <svg className="mcc-success-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="mcc-success-title" role="heading" aria-level={3}>
            {t('initiation.uploadedTitle')}
          </div>
          <p className="mcc-initiation-description">{t('initiation.uploadedHint')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="mcc-initiation-buttons">
          <button
            onClick={handleLater}
            className="button-secondary"
            disabled={isStartingCrossCheck}
          >
            {t('initiation.later')}
          </button>
          <button
            onClick={handleStartCrossCheck}
            disabled={isStartingCrossCheck || (isConstructionLoanFlow && !reviewMode)}
            className="mcc-button-start-cross-check"
          >
            {isStartingCrossCheck ? (
              t('initiation.starting')
            ) : (
              <>
                <svg className="mcc-play-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                {t('initiation.startCrossCheck')}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return null;
};
